<template>
  <ClientOnly>
    <ais-instant-search
      ref="searchRef"
      index-name="listings"
      :search-client="algolia"
      :routing="aisRouter"
      :on-state-change="onStateChange"
    >
      <section
        ref="searchFilters"
        class="md:sticky md:top-[158px] lg:top-[100px] z-20 w-full lg:h-[90px] bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700"
      >
        <div
          class="py-3 lg:py-6 px-4 md:px-8 dark:bg-gray-800 flex flex-wrap gap-2 lg:gap-4 items-center justify-between"
        >
          <div class="flex gap-2 xl:gap-4 flex-wrap">
            <div class="lg:max-w-[180px]" hidden>
              <label
                for="period"
                class="block sr-only mb-2 text-sm font-medium text-gray-900 dark:text-white"
                >Select date</label
              >
              <div class="relative">
                <div
                  class="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none"
                >
                  <svg
                    class="w-4 h-4 ms-1 text-gray-500 dark:text-gray-400"
                    aria-hidden="true"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      d="M0 18a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V8H0v10Zm14-7.5a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1Zm0 4a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1Zm-5-4a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1Zm0 4a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1Zm-5-4a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1Zm0 4a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1ZM20 4a2 2 0 0 0-2-2h-2V1a1 1 0 0 0-2 0v1h-3V1a1 1 0 0 0-2 0v1H6V1a1 1 0 0 0-2 0v1H2a2 2 0 0 0-2 2v2h20V4Z"
                    />
                  </svg>
                </div>
                <input
                  type="text"
                  id="period"
                  class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 lg:min-w-[180px]"
                  placeholder="Select date"
                  autocomplete="off"
                />
              </div>
            </div>
            <div class="xl:max-w-[180px]">
              <label
                for="months"
                class="block mb-2 text-sm font-medium text-gray-900 dark:text-white sr-only"
                >{{$t('labels.period')}}</label
              >
              <ais-numeric-menu
                attribute="packages.min"
                :items="[
                  { label: $t('labels.period'), start: 0 },
                  { label: '1 ' + $t('placeholders.month'), end: 1 },
                  { label: '2 ' + $t('placeholders.months'), end: 2 },
                  { label: '3 ' + $t('placeholders.months'), end: 3 },
                  { label: '4 ' + $t('placeholders.months'), end: 4 },
                  { label: '5 ' + $t('placeholders.months'), end: 5 },
                  { label: '6 ' + $t('placeholders.months'), end: 6 },
                  { label: '12 ' + $t('placeholders.months'), end: 12 },
                ]"
              >
                <template v-slot="{ items, refine, createURL }">
                  <select
                    @change="refine($event.target.value); track('search_filter', { filter: 'period', value: $event.target.value })"
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-fit p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 xl:min-w-[160px] 2xl:min-w-[180px]"
                  >
                    <option
                      v-for="item in items"
                      :value="item.value"
                      :selected="item.isRefined"
                    >
                      {{ item.label }}
                    </option>
                  </select>
                </template>
              </ais-numeric-menu>
            </div>
            <div class="xl:max-w-[180px]">
              <label
                for="bedrooms"
                class="block mb-2 text-sm font-medium text-gray-900 dark:text-white sr-only"
                >{{ $t("placeholders.bedrooms") }}</label
              >
              <ais-menu-select attribute="property.bedrooms">
                <template v-slot="{ items, canRefine, refine, sendEvent }">
                  <select
                    class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-fit p-2.5 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 xl:min-w-[165px] 2xl:min-w-[180px]"
                    slot-scope="{ items, canRefine, refine }"
                    :disabled="!canRefine"
                    @change="refine($event.currentTarget.value); track('search_filter', { filter: 'bedrooms', value: $event.currentTarget.value })"
                  >
                    <option value="">
                      {{ $t("placeholders.bedrooms") }} (min)
                    </option>
                    <option
                      v-for="item in items"
                      :key="item.value"
                      :value="item.value"
                      :selected="item.isRefined"
                    >
                      {{
                        item.label == "0"
                          ? $t("placeholders.studio")
                          : item.label + " " + $t("placeholders.bedrooms")
                      }}
                      ({{ countTotalItemsHigherThan(items, item) }})
                    </option>
                  </select>
                </template>
              </ais-menu-select>
            </div>
            <div class="lg:max-w-[180px]">
              <SearchPriceRangeButton
                attribute="package.monthly_total_rent"
                :label="$t('placeholders.monthly_rent')"
                :currency="websiteConfigurationStore.configuration.currencyCode"
              />
            </div>
            <div class="lg:max-w-[360px]">
              <SearchFeaturesButton
                attribute="features.slug"
                store="features"
                :label="$t('placeholders.features')"
              />
            </div>
          </div>
          <div>
            <button
              class="inline-flex flex-nowrap shrink-0 items-center text-gray-800 dark:text-gray-300 hover:text-blue-800 border border-gray-200 dark:border-gray-700 hover:border-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-3.5 lg:px-5 py-2.5 me-2 xl:me-4 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
            >
              <svg
                class="w-3.5 h-3.5 mr-2"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <path
                  stroke="currentColor"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 5.4V3m0 2.4a5.3 5.3 0 0 1 5.1 5.3v1.8c0 2.4 1.9 3 1.9 4.2 0 .6 0 1.3-.5 1.3h-13c-.5 0-.5-.7-.5-1.3 0-1.2 1.9-1.8 1.9-4.2v-1.8A5.3 5.3 0 0 1 12 5.4ZM8.7 18c.1.9.3 1.5 1 2.1a3.5 3.5 0 0 0 4.6 0c.7-.6 1.3-1.2 1.4-2.1h-7Z"
                />
              </svg>
              {{ $t("actions.create_alert") }}
            </button>
            <button
              data-modal-target="search-filters-modal"
              data-modal-toggle="search-filters-modal"
              @click="searchStore.loadAreas()"
              class="inline-flex flex-nowrap shrink-0 items-center text-gray-800 dark:text-gray-300 hover:text-blue-800 border border-gray-200 dark:border-gray-700 hover:border-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-3.5 lg:px-5 py-2.5 text-center dark:bg-blue-600 dark:hover:bg-blue-700 dark:focus:ring-blue-800"
            >
              <svg
                class="w-3.5 h-3.5 mr-2 text-current"
                xmlns="http://www.w3.org/2000/svg"
                width="14"
                height="15"
                viewBox="0 0 14 15"
                fill="currentColor"
              >
                <path
                  d="M0.7 4.03121H1.6968C1.84221 4.48408 2.12922 4.87936 2.51631 5.15986C2.90339 5.44037 3.37048 5.59156 3.85 5.59156C4.32952 5.59156 4.79661 5.44037 5.18369 5.15986C5.57078 4.87936 5.85779 4.48408 6.0032 4.03121H13.3C13.4857 4.03121 13.6637 3.95812 13.795 3.82801C13.9263 3.69791 14 3.52145 14 3.33745C14 3.15346 13.9263 2.97699 13.795 2.84689C13.6637 2.71678 13.4857 2.64369 13.3 2.64369H6.0032C5.85779 2.19082 5.57078 1.79555 5.18369 1.51504C4.79661 1.23453 4.32952 1.08334 3.85 1.08334C3.37048 1.08334 2.90339 1.23453 2.51631 1.51504C2.12922 1.79555 1.84221 2.19082 1.6968 2.64369H0.7C0.514348 2.64369 0.336301 2.71678 0.205025 2.84689C0.0737498 2.97699 0 3.15346 0 3.33745C0 3.52145 0.0737498 3.69791 0.205025 3.82801C0.336301 3.95812 0.514348 4.03121 0.7 4.03121ZM3.85 2.47025C4.02306 2.47025 4.19223 2.52111 4.33612 2.6164C4.48002 2.71169 4.59217 2.84713 4.65839 3.00559C4.72462 3.16405 4.74195 3.33841 4.70819 3.50663C4.67442 3.67485 4.59109 3.82937 4.46872 3.95065C4.34635 4.07193 4.19044 4.15453 4.0207 4.18799C3.85097 4.22145 3.67504 4.20428 3.51515 4.13864C3.35527 4.073 3.21861 3.96185 3.12246 3.81924C3.02632 3.67663 2.975 3.50897 2.975 3.33745C2.97537 3.10757 3.06768 2.88721 3.23169 2.72465C3.3957 2.5621 3.61805 2.47062 3.85 2.47025Z"
                />
                <path
                  d="M13.3 10.9688H6.0032C5.85779 10.5159 5.57078 10.1207 5.18369 9.84016C4.79661 9.55965 4.32952 9.40846 3.85 9.40846C3.37048 9.40846 2.90339 9.55965 2.51631 9.84016C2.12922 10.1207 1.84221 10.5159 1.6968 10.9688H0.7C0.514348 10.9688 0.336301 11.0419 0.205025 11.172C0.0737498 11.3021 0 11.4786 0 11.6626C0 11.8466 0.0737498 12.023 0.205025 12.1531C0.336301 12.2832 0.514348 12.3563 0.7 12.3563H1.6968C1.84221 12.8092 2.12922 13.2045 2.51631 13.485C2.90339 13.7655 3.37048 13.9167 3.85 13.9167C4.32952 13.9167 4.79661 13.7655 5.18369 13.485C5.57078 13.2045 5.85779 12.8092 6.0032 12.3563H13.3C13.4857 12.3563 13.6637 12.2832 13.795 12.1531C13.9263 12.023 14 11.8466 14 11.6626C14 11.4786 13.9263 11.3021 13.795 11.172C13.6637 11.0419 13.4857 10.9688 13.3 10.9688ZM3.85 12.5298C3.67694 12.5298 3.50777 12.4789 3.36388 12.3836C3.21998 12.2883 3.10783 12.1529 3.04161 11.9944C2.97538 11.836 2.95805 11.6616 2.99181 11.4934C3.02557 11.3252 3.10891 11.1706 3.23128 11.0494C3.35365 10.9281 3.50956 10.8455 3.6793 10.812C3.84903 10.7786 4.02496 10.7957 4.18485 10.8614C4.34473 10.927 4.48139 11.0382 4.57754 11.1808C4.67368 11.3234 4.725 11.4911 4.725 11.6626C4.72463 11.8925 4.63232 12.1128 4.46831 12.2754C4.3043 12.4379 4.08195 12.5294 3.85 12.5298Z"
                />
                <path
                  d="M13.3 6.80625H12.3032C12.1578 6.35338 11.8708 5.9581 11.4837 5.6776C11.0966 5.39709 10.6295 5.2459 10.15 5.2459C9.67048 5.2459 9.20339 5.39709 8.81631 5.6776C8.42922 5.9581 8.14221 6.35338 7.9968 6.80625H0.7C0.514348 6.80625 0.336301 6.87934 0.205025 7.00945C0.0737498 7.13955 0 7.31601 0 7.50001C0 7.68401 0.0737498 7.86047 0.205025 7.99057C0.336301 8.12068 0.514348 8.19377 0.7 8.19377H7.9968C8.14221 8.64664 8.42922 9.04192 8.81631 9.32242C9.20339 9.60293 9.67048 9.75412 10.15 9.75412C10.6295 9.75412 11.0966 9.60293 11.4837 9.32242C11.8708 9.04192 12.1578 8.64664 12.3032 8.19377H13.3C13.4857 8.19377 13.6637 8.12068 13.795 7.99057C13.9263 7.86047 14 7.68401 14 7.50001C14 7.31601 13.9263 7.13955 13.795 7.00945C13.6637 6.87934 13.4857 6.80625 13.3 6.80625ZM10.15 8.36721C9.97694 8.36721 9.80777 8.31635 9.66388 8.22106C9.51998 8.12577 9.40783 7.99033 9.34161 7.83187C9.27538 7.67341 9.25805 7.49905 9.29181 7.33083C9.32557 7.16261 9.40891 7.00809 9.53128 6.88681C9.65365 6.76553 9.80956 6.68293 9.9793 6.64947C10.149 6.61601 10.325 6.63319 10.4848 6.69882C10.6447 6.76446 10.7814 6.87561 10.8775 7.01822C10.9737 7.16083 11.025 7.32849 11.025 7.50001C11.0246 7.72989 10.9323 7.95026 10.7683 8.11281C10.6043 8.27536 10.382 8.36684 10.15 8.36721Z"
                />
              </svg>
              {{ $t("actions.more_filters") }}
            </button>
          </div>
        </div>
      </section>

      <section class="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div class="grid grid-cols-1 md:grid-cols-12">
          <div
            class="px-4 rounded-t-xl md:px-8 pb-5 md:pb-10 order-2 md:order-1 relative z-10 bg-white dark:bg-gray-900"
            :class="listingsClass"
          >
            <nav class="flex items-center md:items-end justify-between mb-4">
              <div class="border-b border-gray-200 dark:border-gray-700">
                <ul
                  class="flex flex-wrap gap-8 -mb-px text-sm font-medium text-center"
                  id="default-tab"
                  role="tablist"
                >
                  <li role="presentation">
                    <button
                      class="inline-block py-4 border-b"
                      :class="activeTab == 'properties' ? activeClasses : ''"
                      @click="switchTab('properties')"
                      type="button"
                      role="tab"
                      aria-controls="rent"
                      :aria-selected="activeTab == 'properties'"
                    >
                      {{ $t("tabs.properties") }}
                      <ais-state-results
                        v-if="activeTab == 'properties'"
                        :class-names="{ 'ais-StateResults': 'inline' }"
                      >
                        <template v-slot="{ status, results }">
                          <span
                            v-if="status === 'idle' && results"
                            class="ms-1 inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                            >{{ results.nbHits.toLocaleString() }}</span
                          >
                          <span v-else></span>
                        </template>
                      </ais-state-results>
                    </button>
                  </li>
                  <li role="presentation">
                    <button
                      class="inline-block py-4 border-b"
                      :class="activeTab == 'projects' ? activeClasses : ''"
                      @click="switchTab('projects')"
                      id="projects-tab"
                      type="button"
                      role="tab"
                      aria-controls="rent"
                      :aria-selected="activeTab == 'projects'"
                    >
                      {{ $t("tabs.buildings") }}
                      <ais-state-results
                        v-if="activeTab == 'projects'"
                        :class-names="{ 'ais-StateResults': 'inline' }"
                      >
                        <template v-slot="{ status, results }">
                          <span
                            v-if="status === 'idle' && results"
                            class="ms-1 inline-flex items-center justify-center px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
                            >{{ results.nbHits.toLocaleString() }}</span
                          >
                          <span v-else></span>
                        </template>
                      </ais-state-results>
                    </button>
                  </li>
                </ul>
              </div>
              <div class="flex items-center gap-4">
                <div
                  class="hidden md:inline-flex rounded-md shadow-sm"
                  role="group"
                >
                  <button
                    type="button"
                    @click="view = 'list'"
                    :class="(view == 'list') ? 'bg-gray-100' : 'bg-white'"
                    class="px-4 py-2 text-sm font-medium text-gray-900 border border-gray-200 rounded-s-lg hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-blue-500 dark:focus:text-white"
                  >
                    <svg
                      class="w-3 h-3"
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                    >
                      <g clip-path="url(#clip0_824_1174)">
                        <path
                          d="M11.2941 2.07143H0.705882C0.518671 2.07143 0.339127 1.98865 0.206748 1.8413C0.0743697 1.69395 0 1.4941 0 1.28571C0 1.07733 0.0743697 0.877481 0.206748 0.730131C0.339127 0.582781 0.518671 0.5 0.705882 0.5H11.2941C11.4813 0.5 11.6609 0.582781 11.7933 0.730131C11.9256 0.877481 12 1.07733 12 1.28571C12 1.4941 11.9256 1.69395 11.7933 1.8413C11.6609 1.98865 11.4813 2.07143 11.2941 2.07143Z"
                          fill="#4B5563"
                        />
                        <path
                          d="M11.2941 6.78571H0.705882C0.518671 6.78571 0.339127 6.70293 0.206748 6.55558C0.0743697 6.40823 0 6.20838 0 6C0 5.79162 0.0743697 5.59177 0.206748 5.44442C0.339127 5.29707 0.518671 5.21429 0.705882 5.21429H11.2941C11.4813 5.21429 11.6609 5.29707 11.7933 5.44442C11.9256 5.59177 12 5.79162 12 6C12 6.20838 11.9256 6.40823 11.7933 6.55558C11.6609 6.70293 11.4813 6.78571 11.2941 6.78571Z"
                          fill="#4B5563"
                        />
                        <path
                          d="M11.2941 11.5H0.705882C0.518671 11.5 0.339127 11.4172 0.206748 11.2699C0.0743697 11.1225 0 10.9227 0 10.7143C0 10.5059 0.0743697 10.3061 0.206748 10.1587C0.339127 10.0114 0.518671 9.92857 0.705882 9.92857H11.2941C11.4813 9.92857 11.6609 10.0114 11.7933 10.1587C11.9256 10.3061 12 10.5059 12 10.7143C12 10.9227 11.9256 11.1225 11.7933 11.2699C11.6609 11.4172 11.4813 11.5 11.2941 11.5Z"
                          fill="#4B5563"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_824_1174">
                          <rect width="12" height="12" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                  <button
                    type="button"
                    @click="view = 'tiles'"
                    :class="(view == 'tiles') ? 'bg-gray-100' : 'bg-white'"
                    class="px-4 py-2 text-sm font-medium text-gray-900 border border-gray-200 border-l-0 rounded-e-lg hover:bg-gray-100 hover:text-blue-700 focus:z-10 focus:ring-2 focus:ring-blue-700 focus:text-blue-700 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:hover:text-white dark:hover:bg-gray-600 dark:focus:ring-blue-500 dark:focus:text-white"
                  >
                    <svg
                      class="w-3 h-3"
                      xmlns="http://www.w3.org/2000/svg"
                      width="14"
                      height="14"
                      viewBox="0 0 14 14"
                      fill="none"
                    >
                      <path
                        d="M5.03581 1.5H2.08919C1.76379 1.5 1.5 1.76379 1.5 2.08919V5.03581C1.5 5.36121 1.76379 5.625 2.08919 5.625H5.03581C5.36121 5.625 5.625 5.36121 5.625 5.03581V2.08919C5.625 1.76379 5.36121 1.5 5.03581 1.5Z"
                        fill="white"
                      />
                      <path
                        d="M11.9108 1.5H8.96419C8.63879 1.5 8.375 1.76379 8.375 2.08919V5.03581C8.375 5.36121 8.63879 5.625 8.96419 5.625H11.9108C12.2362 5.625 12.5 5.36121 12.5 5.03581V2.08919C12.5 1.76379 12.2362 1.5 11.9108 1.5Z"
                        fill="white"
                      />
                      <path
                        d="M5.03581 8.375H2.08919C1.76379 8.375 1.5 8.63879 1.5 8.96419V11.9108C1.5 12.2362 1.76379 12.5 2.08919 12.5H5.03581C5.36121 12.5 5.625 12.2362 5.625 11.9108V8.96419C5.625 8.63879 5.36121 8.375 5.03581 8.375Z"
                        fill="white"
                      />
                      <path
                        d="M11.9108 8.375H8.96419C8.63879 8.375 8.375 8.63879 8.375 8.96419V11.9108C8.375 12.2362 8.63879 12.5 8.96419 12.5H11.9108C12.2362 12.5 12.5 12.2362 12.5 11.9108V8.96419C12.5 8.63879 12.2362 8.375 11.9108 8.375Z"
                        fill="white"
                      />
                      <path
                        d="M5.03581 1.5H2.08919C1.76379 1.5 1.5 1.76379 1.5 2.08919V5.03581C1.5 5.36121 1.76379 5.625 2.08919 5.625H5.03581C5.36121 5.625 5.625 5.36121 5.625 5.03581V2.08919C5.625 1.76379 5.36121 1.5 5.03581 1.5Z"
                        stroke="#4B5563"
                        stroke-width="1.4"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M11.9108 1.5H8.96419C8.63879 1.5 8.375 1.76379 8.375 2.08919V5.03581C8.375 5.36121 8.63879 5.625 8.96419 5.625H11.9108C12.2362 5.625 12.5 5.36121 12.5 5.03581V2.08919C12.5 1.76379 12.2362 1.5 11.9108 1.5Z"
                        stroke="#4B5563"
                        stroke-width="1.4"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M5.03581 8.375H2.08919C1.76379 8.375 1.5 8.63879 1.5 8.96419V11.9108C1.5 12.2362 1.76379 12.5 2.08919 12.5H5.03581C5.36121 12.5 5.625 12.2362 5.625 11.9108V8.96419C5.625 8.63879 5.36121 8.375 5.03581 8.375Z"
                        stroke="#4B5563"
                        stroke-width="1.4"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                      <path
                        d="M11.9108 8.375H8.96419C8.63879 8.375 8.375 8.63879 8.375 8.96419V11.9108C8.375 12.2362 8.63879 12.5 8.96419 12.5H11.9108C12.2362 12.5 12.5 12.2362 12.5 11.9108V8.96419C12.5 8.63879 12.2362 8.375 11.9108 8.375Z"
                        stroke="#4B5563"
                        stroke-width="1.4"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                </div>
                <label class="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    v-model="showMap"
                    class="sr-only peer"
                    checked
                  />
                  <div
                    class="w-9 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"
                  ></div>
                  <span
                    class="ms-3 text-sm font-medium text-gray-900 dark:text-gray-300"
                    >{{ $t("labels.map") }}</span
                  >
                </label>
              </div>
            </nav>

            <div id="search-tab-content">
              <div
                class=""
                id="rent"
                role="tabpanel"
                aria-labelledby="rent-tab"
              >
                <ais-state-results>
                  <template v-slot="{ status, results }">
                    <!-- Loading skeleton -->
                    <div
                      v-if="status === 'loading' || status === 'stalled'"
                      :class="
                        view == 'list'
                          ? 'block space-y-5'
                          : 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 min-[1650px]:grid-cols-4 gap-4 xl:gap-8'
                      "
                    >
                      <template v-if="view == 'list'">
                        <CardHorizontalSkeleton v-for="n in 6" :key="n" />
                      </template>
                      <template v-else>
                        <CardPropertySkeleton v-for="n in 12" :key="n" />
                      </template>
                    </div>

                    <!-- No results found -->
                    <div
                      v-else-if="results && results.nbHits === 0"
                      class="flex flex-col items-center justify-center text-center py-16 px-4"
                    >
                      <svg
                        class="w-16 h-16 mb-4 text-gray-300 dark:text-gray-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          stroke-width="1.5"
                          d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z"
                        />
                      </svg>
                      <h3 class="text-lg font-semibold text-gray-900 dark:text-white">
                        {{ $t("search.no_results_title") }}
                      </h3>
                      <p class="mt-2 max-w-md text-sm text-gray-500 dark:text-gray-400">
                        {{ $t("search.no_results_message") }}
                      </p>
                    </div>

                    <!-- Results (rendered directly from results.hits to avoid
                         mounting/unmounting ais-hits on every search status change) -->
                    <div
                      v-else-if="results"
                      :class="
                        view == 'list'
                          ? 'block space-y-5'
                          : 'grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 min-[1650px]:grid-cols-4 gap-4 xl:gap-8'
                      "
                    >
                      <template v-if="activeTab == 'properties'">
                        <template v-if="view == 'tiles'">
                          <CardProperty
                            v-for="hit in results.hits"
                            :key="hit.objectID"
                            :listing="hit"
                          />
                        </template>
                        <SearchPropertyListResults
                          v-else
                          :hits="results.hits"
                        />
                      </template>
                      <template v-else>
                        <template v-if="view == 'tiles'">
                          <CardProject
                            v-for="hit in results.hits"
                            :key="hit.objectID"
                            :listing="hit"
                          />
                        </template>
                        <SearchProjectListResults
                          v-else
                          :hits="results.hits"
                        />
                      </template>
                    </div>
                  </template>
                </ais-state-results>

                <nav aria-label="Pagination">
                  <ais-pagination @page-change="scrollToTop" class="flex justify-center items-center">
                    <template
                      v-slot="{
                        currentRefinement,
                        nbPages,
                        pages,
                        isFirstPage,
                        isLastPage,
                        refine,
                        createURL,
                      }"
                    >
                      <ul
                        v-show="nbPages > 1"
                        class="mt-5 md:mt-10 inline-flex -space-x-px text-sm"
                      >
                        <li v-if="!isFirstPage" hidden>
                          <a
                            :href="createURL(0)"
                            @click.prevent="refine(0)"
                            class="flex items-center justify-center px-3 h-8 ms-0 leading-tight text-gray-500 bg-white border border-e-0 border-gray-300 rounded-s-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="currentColor"
                            >
                              <path
                                stroke="currentColor"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M5 1 1 5l4 4m6-8L7 5l4 4"
                              />
                            </svg>
                          </a>
                        </li>

                        <li v-if="!isFirstPage">
                          <a
                            :href="createURL(currentRefinement - 1)"
                            @click.prevent="refine(currentRefinement - 1)"
                            class="flex items-center justify-center px-3 h-8 ms-0 leading-tight text-gray-500 bg-white border border-e-0 border-gray-300 rounded-s-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="currentColor"
                            >
                              <path
                                d="M8.32137 12C8.0126 11.9999 7.71649 11.8736 7.49819 11.6488L2.8409 6.85472C2.62262 6.62997 2.5 6.32517 2.5 6.00736C2.5 5.68956 2.62262 5.38476 2.8409 5.16001L7.49819 0.365896C7.6056 0.251425 7.73407 0.160118 7.87613 0.0973047C8.01818 0.034491 8.17096 0.00142813 8.32556 4.52529e-05C8.48016 -0.00133763 8.63347 0.0289869 8.77657 0.0892499C8.91966 0.149513 9.04965 0.238507 9.15898 0.35104C9.2683 0.463573 9.35475 0.597391 9.4133 0.744686C9.47184 0.89198 9.5013 1.0498 9.49996 1.20894C9.49861 1.36808 9.46649 1.52535 9.40547 1.67158C9.34445 1.8178 9.25575 1.95005 9.14455 2.06061L5.31043 6.00736L9.14455 9.95411C9.30733 10.1217 9.41818 10.3353 9.46308 10.5677C9.50799 10.8002 9.48493 11.0411 9.39683 11.2601C9.30872 11.4791 9.15953 11.6662 8.9681 11.7979C8.77668 11.9296 8.55161 11.9999 8.32137 12Z"
                              />
                            </svg>
                          </a>
                        </li>
                        <li v-for="page in pages" :key="page">
                          <a
                            :href="createURL(page)"
                            @click.prevent="refine(page)"
                            class="flex items-center justify-center px-3 h-8 leading-tight border border-gray-300"
                            :class="{
                              'text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700 dark:border-gray-700 dark:bg-gray-700 dark:text-white':
                                page === currentRefinement,
                              'text-gray-500 bg-white hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white':
                                page !== currentRefinement,
                            }"
                            >{{ page + 1 }}</a
                          >
                        </li>
                        <li v-if="!isLastPage">
                          <a
                            :href="createURL(currentRefinement + 1)"
                            @click.prevent="refine(currentRefinement + 1)"
                            class="flex items-center justify-center px-3 h-8 leading-tight text-gray-500 bg-white border border-gray-300 rounded-e-lg hover:bg-gray-100 hover:text-gray-700 dark:bg-gray-800 dark:border-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-white"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="12"
                              height="12"
                              viewBox="0 0 12 12"
                              fill="currentColor"
                            >
                              <path
                                d="M3.67863 12C3.44839 11.9999 3.22332 11.9296 3.0319 11.7979C2.84047 11.6662 2.69128 11.4791 2.60317 11.2601C2.51507 11.0411 2.49201 10.8002 2.53692 10.5677C2.58182 10.3353 2.69267 10.1217 2.85546 9.95411L6.68957 6.00736L2.85546 2.06061C2.74425 1.95005 2.65555 1.8178 2.59453 1.67158C2.53351 1.52535 2.50139 1.36808 2.50004 1.20894C2.4987 1.0498 2.52816 0.89198 2.5867 0.744686C2.64525 0.597391 2.7317 0.463573 2.84102 0.35104C2.95034 0.238507 3.08034 0.149513 3.22343 0.0892499C3.36653 0.0289869 3.51984 -0.00133763 3.67444 4.52529e-05C3.82904 0.00142813 3.98182 0.034491 4.12387 0.0973047C4.26593 0.160118 4.3944 0.251425 4.50181 0.365896L9.1591 5.16001C9.37738 5.38476 9.5 5.68956 9.5 6.00736C9.5 6.32517 9.37738 6.62997 9.1591 6.85472L4.50181 11.6488C4.28351 11.8736 3.9874 11.9999 3.67863 12Z"
                              />
                            </svg>
                          </a>
                        </li>
                        <li v-if="!isLastPage" hidden>
                          <a
                            :href="createURL(nbPages)"
                            @click.prevent="refine(nbPages)"
                          >
                            ››
                          </a>
                        </li>
                      </ul>
                    </template>
                  </ais-pagination>
                </nav>
              </div>
            </div>
          </div>
          <div
            class="grid-cols-1 md:col-span-4 relative order-1 -mb-10 md:mb-0 md:order-2 relative"
            v-if="showMap"
          >
            <div
              class="w-full h-96 md:sticky md:top-[224px] lg:top-[190px] md:h-[calc(100vh-224px)] lg:h-[calc(100vh-190px)]"
            >
              <div
                class="absolute bottom-[3.25rem] md:bottom-4 left-3 md:left-4 z-10 hidden"
              >
                <button
                  type="button"
                  class="inline-flex items-center text-white bg-gray-800 hover:bg-gray-900 focus:outline-none focus:ring-4 focus:ring-gray-300 font-medium rounded-lg text-sm px-5 py-2.5 me-2 mb-2 dark:bg-gray-800 dark:hover:bg-gray-700 dark:focus:ring-gray-700 dark:border-gray-700"
                >
                  <svg
                    class="w-3.5 h-3.5 mr-2"
                    xmlns="http://www.w3.org/2000/svg"
                    width="14"
                    height="15"
                    viewBox="0 0 14 15"
                    fill="none"
                  >
                    <path
                      fill-rule="evenodd"
                      clip-rule="evenodd"
                      d="M2.09998 4.69998C2.09998 4.14302 2.32123 3.60888 2.71505 3.21505C3.10888 2.82123 3.64302 2.59998 4.19998 2.59998H11.2C11.33 2.59998 11.4574 2.63618 11.568 2.70452C11.6786 2.77286 11.7679 2.87065 11.8261 2.98693C11.8842 3.1032 11.9088 3.23337 11.8971 3.36284C11.8855 3.49231 11.838 3.61598 11.76 3.71998L9.97498 6.09998L11.76 8.47998C11.838 8.58397 11.8855 8.70764 11.8971 8.83711C11.9088 8.96658 11.8842 9.09675 11.8261 9.21302C11.7679 9.3293 11.6786 9.42709 11.568 9.49543C11.4574 9.56378 11.33 9.59998 11.2 9.59998H4.19998C4.01432 9.59998 3.83628 9.67373 3.705 9.805C3.57373 9.93628 3.49998 10.1143 3.49998 10.3V12.4C3.49998 12.5856 3.42623 12.7637 3.29495 12.895C3.16367 13.0262 2.98563 13.1 2.79998 13.1C2.61432 13.1 2.43628 13.0262 2.305 12.895C2.17373 12.7637 2.09998 12.5856 2.09998 12.4V4.69998Z"
                      fill="white"
                    />
                  </svg>
                  Add pin
                </button>
              </div>
              <AisMap :bounds="location.bounds" />
            </div>
          </div>
        </div>
      </section>

      <SearchFilters />
      <ais-configure
        :hits-per-page.camel="24"
        :distinct="activeTab == 'projects'"
        :filters="filters"
      ></ais-configure>
    </ais-instant-search>
  </ClientOnly>
</template>

<script setup lang="ts">
import {
  AisInstantSearch,
  AisNumericMenu,
  AisSearchBox,
  AisHits,
  AisInfiniteHits,
  AisMenuSelect,
  AisConfigure,
  AisPagination,
  AisStats,
  AisStateResults,
  AisRefinementList,
  AisHighlight,
} from "vue-instantsearch/vue3/es";
import { useSearchModalStore } from "@/stores/searchModalStore";
import { ref, watch, onMounted, onBeforeUnmount, watchEffect } from "vue";
import { initModals, initTabs } from "flowbite";
import { useSearchStore } from "~/stores/searchStore";
import { useRoute } from "vue-router";
const { $helpers } = useNuxtApp();

import { createWidgetMixin } from "vue-instantsearch/vue3/es";
import { connectGeoSearch } from "instantsearch.js/es/connectors";
import { useWebsiteConfigurationStore } from "~/stores/websiteConfigurationStore";

const { locale, t, te } = useI18n();
const websiteConfigurationStore = useWebsiteConfigurationStore();
const searchStore = useSearchStore();
const { track } = useAnalytics();
const algolia = useAlgoliaRef();
const store = useSearchModalStore();
const showMap = ref(true);
const listingsClass = ref("col-span-8");
const router = useRouter();
const aisRouter = routing();
const route = useRoute();
const searchRef = ref(null);
// The active tab lives in the URL (?tab=buildings) so copied links open the
// right tab; the properties tab is the unmarked default.
const activeTab = ref(route.query.tab == "buildings" ? "projects" : "properties");
const view = ref(process.client ? (localStorage.getItem("searchView") || "tiles") : "tiles");
const activeClasses =
  "text-blue-600 hover:text-blue-600 dark:text-blue-500 dark:hover:text-blue-500 border-blue-600 dark:border-blue-500";

import { history as historyRouter } from "instantsearch.js/es/lib/routers";
import { simple as simpleMapping } from "instantsearch.js/es/lib/stateMappings";

function switchTab(tab) {
  if (activeTab.value === tab) return;
  activeTab.value = tab;
  track("search_tab_switch", { tab });
  // Each tab paginates a different result set, so a page number carried over
  // from the previous tab would point at unrelated (or missing) results.
  // Only touch the state when a page is actually set — the `distinct` change
  // already triggers a search, and an unconditional setUiState would add a
  // second full render of every widget.
  const instance = searchRef.value?.instantSearchInstance;
  if (instance && instance.getUiState().listings?.page) {
    instance.setUiState((uiState) => {
      const { page, ...listings } = uiState.listings ?? {};
      return { ...uiState, listings };
    });
  }
}

function routing() {
  var mapping = simpleMapping();

  var clean = function clean(obj) {
    for (var propName in obj) {
      if (obj[propName] === null || obj[propName] === undefined) {
        delete obj[propName];
      }
    }
    return obj;
  };

  (mapping.stateToRoute = function (uiState) {
    uiState = uiState["listings"];
    var route = {
      bedrooms: uiState.menu ? uiState.menu["property.bedrooms"] : null,
      bathrooms: uiState.menu ? uiState.menu["property.bathrooms"] : null,
      min_stay: uiState.numericMenu
        ? uiState.numericMenu["packages.min"]
        : null,
      rent: uiState.range
        ? uiState.range["package.monthly_total_rent"]
        : null,
      page: uiState.page,
      // The tab is component state rather than InstantSearch state, but it
      // has to travel through this mapping — the history router owns the
      // query string and would drop any param written by vue-router.
      tab: activeTab.value == "projects" ? "buildings" : null,
    };

    if (uiState.refinementList && uiState.refinementList["location.geocode"]) {
      route.geocode = uiState.refinementList["location.geocode"].join();
    }

    if (uiState.refinementList && uiState.refinementList["features.slug"]) {
      route.features = uiState.refinementList["features.slug"].join();
    }

    if (
      uiState.refinementList &&
      uiState.refinementList["location.district.slug"]
    ) {
      route.districts = uiState.refinementList["location.district.slug"].join();
    }

    if (uiState.geoSearch && uiState.geoSearch.boundingBox) {
      route.bounds = "@" + uiState.geoSearch.boundingBox;
    }
    return clean(route);
  }),
    (mapping.routeToState = function (route) {
      // Runs on load and on browser back/forward; keeps the tab in sync with
      // the URL the user navigated to.
      activeTab.value = route.tab == "buildings" ? "projects" : "properties";

      if (!route.menu) {
        route.menu = {};
      }
      if (!route.numericMenu) {
        route.numericMenu = {};
      }

      var refinementList = {};

      if (route.geocode) {
        refinementList = {
          ...refinementList,
          "location.geocode": route.geocode.split(","),
        };
      }

      if (route.districts) {
        refinementList = {
          ...refinementList,
          "location.district.slug": route.districts.split(","),
        };
      }

      if (route.profile) {
        refinementList = {
          ...refinementList,
          "profiles.guid": [route.profile],
        };
      }

      if (route.announces) {
        refinementList = {
          ...refinementList,
          announce_guid: route.announces.split(","),
        };
      }

      if (route.features) {
        refinementList = {
          ...refinementList,
          "features.slug": route.features.split(","),
        };
      }

      if (route.rent) {
        route.range = {
          "package.monthly_total_rent": route.rent,
        };
      }

      if (location.cities) {
        refinementList = {
          ...refinementList,
          "location.city.slug": location.cities,
        };
      }

      if (Object.keys(refinementList).length !== 0) {
        route.refinementList = refinementList;
      }

      if (route.bounds) {
        route.geoSearch = {
          boundingBox: route.bounds.replace("@", ""),
        };
      } else if (location.bounds) {
        route.geoSearch = {
          boundingBox: location.bounds,
        };
      }

      if (location.around) {
        // Temporary suspended. Unconsisten behavior config <> route
        // @TODO: around need to reset on search.
        // route.configure = {
        //   aroundLatLng: location.around
        // }
      }

      route.menu["property.bedrooms"] = route.bedrooms;
      route.menu["property.bathrooms"] = route.bathrooms;
      route.numericMenu["packages.min"] = route.min_stay;

      return { listings: route };
    });

  return (routing = {
    router: historyRouter(),
    stateMapping: mapping,
  });
}

function onStateChange({ uiState, setUiState }) {
  // Fire a GA4 `search` event only when the free-text query actually changes,
  // so we capture search terms without emitting an event on every filter tweak
  // (individual filters are tracked separately via `search_filter`).
  const prevQuery = searchStore.uiState?.listings?.query || "";
  const nextQuery = uiState?.listings?.query || "";
  if (nextQuery !== prevQuery && nextQuery) {
    track("search", { search_term: nextQuery });
  }
  searchStore.setUiState(uiState);
  setUiState(uiState);
}

function scrollToTop(page) {
  window.scrollTo(0, 0);
  track("search_pagination", { page });
}

function countTotalItemsLowerThan(inputs, currentInput) {
  // Initialize the sum of counts
  let total = 0;

  // Loop through each input in the inputs array
  for (let input of inputs) {
    // Check if the value of the input is greater than or equal to currentInput
    if (input.value <= currentInput.value) {
      // Add the count of this input to the total
      total += input.count;
    }
  }

  // Return the total sum
  return total;
}

function countTotalItemsHigherThan(inputs, currentInput) {
  // Initialize the sum of counts
  let total = 0;

  // Loop through each input in the inputs array
  for (let input of inputs) {
    // Check if the value of the input is greater than or equal to currentInput
    if (input.value >= currentInput.value) {
      // Add the count of this input to the total
      total += input.count;
    }
  }

  // Return the total sum
  return total;
}

watch(showMap, (newValue) => {
  if (newValue) {
    listingsClass.value = "col-span-8";
  } else {
    listingsClass.value = "col-span-12";
  }
  track("search_map_toggle", { shown: newValue });
});

watch(view, (newValue) => {
  if (process.client) {
    localStorage.setItem("searchView", newValue);
  }
  track("search_view_toggle", { view: newValue });
});

async function fetchLocation() {
  if (route.params.slug.startsWith("@")) {
    return { bounds: route.params.slug.replace("@", "") };
  }
  let locations = $helpers.getConfigurationKey('locations');
  let location = $helpers.getConfigurationKey('locations').find((locationConfig) => locationConfig.name == route.params.slug);

  if (location) {
    if (location.type && location.type == 'region') {
      let cities = locations.filter(locationConfig => locationConfig.region == route.params.slug).map(locationConfig => {return locationConfig.name;});
      return {
        cities: cities,
        bounds: location.bounds
      };
    }
    return {
      cities: [location.name],
      bounds: location.bounds
    };
  }



  let response = await searchStore.findLocationBySlug(route.params.slug);
  return response;
}

// Keep the header sticky while the search page is mounted. The flag stays true
// through navigation (the old page unmounts only once the next page is ready),
// which prevents the header from briefly losing its sticky position when
// clicking through to a property page.
const onSearchPage = useState("onSearchPage", () => true);
onMounted(() => {
  onSearchPage.value = true;
});
onBeforeUnmount(() => {
  onSearchPage.value = false;
});

const location = await fetchLocation();
const filters = `announce_type:property-announces AND language_code:${websiteConfigurationStore.configuration.languageCode} AND listing_id:${websiteConfigurationStore.configuration.listingId}`;

// Build a human-readable location name for SEO: prefer a translated city name,
// otherwise title-case the slug. Bounding-box slugs (prefixed with "@") aren't a
// real place, so fall back to the country for those.
const countryName = t('countries.' + websiteConfigurationStore.configuration.countryCode);
const slug = route.params.slug;
const isBounds = slug.startsWith('@');
const locationName = isBounds
  ? countryName
  : te('cities.' + slug)
    ? t('cities.' + slug)
    : slug.replaceAll('-', ' ').replace(/^([a-z])|\s+([a-z])/g, ($1) => $1.toUpperCase());

const seoTitle = (isBounds
  ? t('seo.for_rent_title').replace(' - [country]', '')
  : t('seo.for_rent_title').replace('[country]', countryName)
).replace('[location]', locationName);
const seoDescription = t('seo.for_rent_description')
  .replace('[location]', locationName)
  .replace('[country]', countryName);
const seoUrl = $helpers.canonicalUrl(route.path);
const seoImage = $helpers.canonicalUrl('/images/logo.png');

useHead({
  title: seoTitle,
  meta: [
    { name: 'description', content: seoDescription },
    // Open Graph (Facebook, WhatsApp, LinkedIn, …)
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: seoTitle },
    { property: 'og:description', content: seoDescription },
    ...(seoUrl ? [{ property: 'og:url', content: seoUrl }] : []),
    ...(seoImage ? [{ property: 'og:image', content: seoImage }] : []),
    // Twitter / X
    { name: 'twitter:card', content: seoImage ? 'summary_large_image' : 'summary' },
    { name: 'twitter:title', content: seoTitle },
    { name: 'twitter:description', content: seoDescription },
    ...(seoImage ? [{ name: 'twitter:image', content: seoImage }] : []),
  ],
});

</script>
